import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { FaBackward, FaPlus, FaSearch } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import { useNavigate, useLocation } from "react-router-dom";
import getCurrentSkin from "../../utils/getCurrentSkin";

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Split combined "itemcode-itemname" string
// e.g. "100000002-AMW CABIN BOUNET CABLE"
// ─────────────────────────────────────────────────────────────────────────────
const splitItemCodeName = (combined = "") => {
  const trimmed = String(combined || "").trim();
  const hyphenIndex = trimmed.indexOf("-");
  if (hyphenIndex === -1) {
    return { code: "", name: trimmed };
  }
  return {
    code: trimmed.slice(0, hyphenIndex).trim(),
    name: trimmed.slice(hyphenIndex + 1).trim(),
  };
};

const StoreRequisitionAdd = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const storeRequestIdFromState = location.state?.storeRequestId || null;
  const storeRequestNoFromState = location.state?.storeRequestNo || "";
  const isEditMode = !!(storeRequestIdFromState || storeRequestNoFromState);

  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ---------------- localStorage values ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch =
    localStorage.getItem("branchType") || localStorage.getItem("branch") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [currentStoreRequestId, setCurrentStoreRequestId] = useState(
    storeRequestIdFromState || null
  );

  const [storeReqNo, setStoreReqNo] = useState(storeRequestNoFromState || "");
  const [date, setDate] = useState("");
  const [fromStore, setFromStore] = useState("");
  const [employee, setEmployee] = useState("");
  const [indentRef, setIndentRef] = useState("");
  const [remarks, setRemarks] = useState("");
  const [vehicleDept, setVehicleDept] = useState("Department");
  const [vehicleType, setVehicleType] = useState("");
  const [isClosed, setIsClosed] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);

  const [storeList, setStoreList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [itemCategories, setItemCategories] = useState([]);

  const [itemCategory, setItemCategory] = useState("");
  const [itemName, setItemName] = useState("");
  const [itemCode, setItemCode] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  const [selectedItems, setSelectedItems] = useState([]);

  const normalizeDateForInput = (value) => {
    if (!value) return "";
    const s = String(value);

    if (s.includes("T")) {
      return s.split("T")[0];
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      return s;
    }

    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
      const [dd, mm, yyyy] = s.split("/");
      return `${yyyy}-${mm}-${dd}`;
    }

    const d = new Date(s);
    if (!isNaN(d.getTime())) {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }

    return "";
  };

  const normalizeSelectedGridItem = (item = {}) => ({
    fld_itemid: item.fld_itemid || item.itemid || item.itemId || 0,
    fld_itemname: item.fld_itemname || item.itemname || "",
    fld_itemcode: item.fld_itemcode || item.itemcode || "",
    fld_itemPartNo:
      item.fld_itemPartNo || item.fld_itempartno || item.partNo || "",
    fld_branchitemid: item.fld_branchitemid || item.branchitemid || null,
    fld_uomname: item.fld_uomname || item.itemuom || "",
    fld_closing_stock:
      item.fld_closing_stock !== undefined && item.fld_closing_stock !== null
        ? item.fld_closing_stock
        : "",
    fld_Qty:
      item.fld_Qty !== undefined && item.fld_Qty !== null
        ? item.fld_Qty
        : item.fld_qty || item.fld_RequestQty || "",
    fld_duedate: normalizeDateForInput(item.fld_duedate || item.fld_date || ""),
    fld_departmentcode: item.fld_departmentcode || "",
    fld_vehichlecode: item.fld_vehichlecode || item.fld_vehiclecode || "",
    fld_Remarks: item.fld_Remarks || item.fld_remarks || "",
    selected:
      item.selected !== undefined && item.selected !== null
        ? item.selected
        : true,
  });

  useEffect(() => {
    if (!branchId) {
      toast.error("BranchId not found in localStorage");
      return;
    }

    axios
      .get("https://nissi-india.com/api/storeReq/si_ddlfiledata", {
        params: { branchid: branchId },
        headers: commonHeaders,
      })
      .then((res) => {
        setStoreList(res.data.stores || []);
        setEmployeeList(res.data.employees || []);
      })
      .catch(() => toast.error("Failed to load store/employee dropdowns"));

    axios
      .get("https://nissi-india.com/api/storeReq/si_itemcategory", {
        headers: commonHeaders,
      })
      .then((res) => setItemCategories(res.data.categories || []))
      .catch(() => toast.error("Failed to load item categories"));

    if (!isEditMode) {
      if (!userId) {
        toast.error("UserId not found in localStorage");
        return;
      }

      axios
        .post(
          "https://nissi-india.com/api/storeReq/si_generateStoreRequestNo",
          {
            storeIssueno: "0",
            userid: userId,
          },
          {
            headers: commonHeaders,
          }
        )
        .then((res) => setStoreReqNo(res.data.code || ""))
        .catch(() => toast.error("Failed to generate request number"));
    } else {
      const params = storeRequestNoFromState
        ? { storeReqNo: storeRequestNoFromState }
        : { storeReqId: storeRequestIdFromState };

      axios
        .get("https://nissi-india.com/api/storeReq/si_existingData", {
          params,
          headers: commonHeaders,
        })
        .then((res) => {
          if (res.data.success) {
            const m = res.data.master || {};
            const ui = res.data.ui || {};

            setCurrentStoreRequestId(m.storeRequestId || null);
            setStoreReqNo(m.storeRequestNo || storeRequestNoFromState || "");
            setDate(normalizeDateForInput(m.dateIssued));
            setFromStore(String(m.storeId || ""));
            setEmployee(String(m.employeeId || ""));
            setIndentRef(m.indentRefNo || "");
            setRemarks(m.remarks || "");
            setIsClosed(!!m.isClosed);
            setIsCancelled(!!m.isCanceled);

            if (ui.showVehicle) {
              setVehicleDept("Vehicle");
              if (ui.showVehicleHigher) setVehicleType("H");
              else if (ui.showVehicleLower) setVehicleType("O");
            } else {
              setVehicleDept("Department");
              setVehicleType("");
            }

            const items = (res.data.items || []).map((item) =>
              normalizeSelectedGridItem({ ...item, selected: true })
            );
            setSelectedItems(items);
          }
        })
        .catch(() => toast.error("Failed to load existing requisition data"));
    }
  }, [
    branchId,
    isEditMode,
    storeRequestIdFromState,
    storeRequestNoFromState,
    userId,
    commonHeaders,
  ]);

  const handleSearch = () => {
    if (!fromStore || fromStore === "Select") {
      toast.warning("Please select From Store first");
      return;
    }

    const payload = {
      branchId,
      categoryId: itemCategory || "",
      itemCode: itemCode || "",
      itemName: itemName || "",
      store: fromStore || "",
    };

    axios
      .get("https://nissi-india.com/api/storeReq/si_searchitem", {
        params: payload,
        headers: commonHeaders,
      })
      .then((res) => {
        const items = (res.data.items || []).map((item) => {
          const { code: resolvedCode, name: resolvedName } = splitItemCodeName(
            item.itemname_itemcode || ""
          );

          return {
            fld_itemid: item.itemid || item.fld_itemid || item.itemId || 0,
            fld_itemcode_raw: resolvedCode,
            fld_itemcode: resolvedCode,
            fld_itemname: resolvedName,
            fld_uomname: item.itemuom || item.fld_uomname || "",
            fld_Qty: item.fld_qty || "",
            fld_duedate: normalizeDateForInput(item.fld_date || ""),
            fld_Remarks: item.fld_remarks || "",
          };
        });

        setSearchResults(items);
        if (items.length === 0) toast.info("No items found.");
      })
      .catch(() => toast.error("Failed to search items"));
  };

  const handleItemSelect = (e, item) => {
    const isChecked = e.target.checked;

    if (!isChecked) {
      setSelectedItems((prev) =>
        prev.filter(
          (i) =>
            String(i.fld_itemcode) !==
            String(item.fld_itemcode_raw || item.fld_itemcode)
        )
      );
      return;
    }

    if (!vehicleDept) {
      toast.warning("Please Select Vehicle/Department first");
      return;
    }

    if (vehicleDept === "Vehicle" && !vehicleType) {
      toast.warning("Please Select Hire or Own Vehicle");
      return;
    }

    const payload = {
      branchid: parseInt(branchId, 10),
      storeid: parseInt(fromStore, 10),
      selectid: String(item.fld_itemcode_raw || item.fld_itemcode),
      existingItems: selectedItems,
    };

    axios
      .post("https://nissi-india.com/api/storeReq/si_fillItemsGrid", payload, {
        headers: commonHeaders,
      })
      .then((res) => {
        const items = (res.data.items || []).map((i) =>
          normalizeSelectedGridItem({
            ...i,
            selected:
              i.selected !== undefined && i.selected !== null ? i.selected : true,
          })
        );
        setSelectedItems(items);
      })
      .catch((err) => {
        console.error("fillItemsGrid error:", err);
        toast.error("Failed to add item to grid");
      });
  };

  const updateSelectedItem = (index, field, value) => {
    setSelectedItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleRowCheckboxChange = (e, itemId) => {
    const isChecked = e.target.checked;
    setSelectedItems((prev) =>
      prev.map((item) =>
        item.fld_itemid === itemId ? { ...item, selected: isChecked } : item
      )
    );
  };

  const handleCloseChange = (checked) => {
    setIsClosed(checked);
    if (checked) setIsCancelled(false);
  };

  const handleCancelChange = (checked) => {
    setIsCancelled(checked);
    if (checked) setIsClosed(false);
  };

  const formatDateToUTC = (dateString) => {
    if (!dateString || isNaN(Date.parse(dateString))) return null;
    const d = new Date(dateString);
    d.setUTCHours(10, 0, 0, 0);
    return d.toISOString().replace(".000", "");
  };

  const handleAdd = () => {
    if (!date) {
      toast.warning("Please select the date");
      return;
    }

    if (!fromStore || fromStore === "Select") {
      toast.warning("Please select From Store");
      return;
    }

    if (!vehicleDept) {
      toast.warning("Please select Vehicle or Department");
      return;
    }

    if (vehicleDept === "Vehicle" && !vehicleType) {
      toast.warning("Please select Hire or Own Vehicle");
      return;
    }

    const itemsToInsert = selectedItems.filter((item) => item.selected);

    if (itemsToInsert.length === 0) {
      toast.warn("Please select at least one item to submit.");
      return;
    }

    const payload = {
      branchId: parseInt(branchId, 10),
      userId: parseInt(userId, 10),
      storeId: parseInt(fromStore, 10),
      requestNo: String(storeReqNo || ""),
      requestDate: formatDateToUTC(date),
      remarks,
      employeeId: employee ? parseInt(employee, 10) : null,
      indentRefNo: indentRef,
      issueFor: vehicleDept === "Vehicle" ? "V" : "D",
      vehicleType: vehicleDept === "Vehicle" ? vehicleType : "",
      isClosed,
      isCancelled,
      storeRequestID: isEditMode ? currentStoreRequestId : null,
      branchWiseNo: isEditMode
        ? undefined
        : Math.floor(1000 + Math.random() * 9000),
      items: itemsToInsert.map((item) => ({
        itemId: item.fld_itemid,
        branchItemId: item.fld_branchitemid,
        requestedQty: Number(item.fld_Qty) || 0,
        dueDate: formatDateToUTC(item.fld_duedate),
        remarks: item.fld_Remarks || "",
        departmentCode: item.fld_departmentcode || "",
        vehicleCode: item.fld_vehichlecode || "",
        selected: true,
      })),
    };

    axios
      .post("https://nissi-india.com/api/storeReq/si_submit", payload, {
        headers: commonHeaders,
      })
      .then(() => {
        toast.success(
          isEditMode
            ? "Store Requisition updated successfully!"
            : "Store Requisition saved successfully!"
        );
        setTimeout(() => navigate("/inventory/StoreRequisition_Listing"), 1500);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.message || "Failed to save requisition.";
        toast.error(msg);
      });
  };

  return (
    <LayoutWrapper>
      <HeaderBar title="STORE REQUISITION" />
      <ToastContainer />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-black"
            : "bg-white text-black"
        }`}
      >
        <div className="flex justify-end mb-4">
          <button
            onClick={() => navigate(-1)}
            className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded transition-colors`}
          >
            <FaBackward /> Back
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block mb-1">Store Req No</label>
            <input
              type="text"
              value={storeReqNo}
              readOnly
              className="w-full border px-2 py-1 bg-gray-100"
            />

            <label className="block mt-3">Date *</label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full border px-2 py-1"
            />

            <label className="block mt-3">From Store *</label>
            <select
              value={fromStore}
              onChange={(e) => setFromStore(e.target.value)}
              className="w-full border px-2 py-1"
            >
              <option value="">Select</option>
              {storeList.map((store, idx) => (
                <option key={idx} value={store.fld_storeid}>
                  {store.fld_storename}
                </option>
              ))}
            </select>

            <label className="block mt-3">Vehicle / Department *</label>
            <div className="flex gap-4 mt-1">
              <label>
                <input
                  type="radio"
                  value="Vehicle"
                  checked={vehicleDept === "Vehicle"}
                  onChange={() => {
                    setVehicleDept("Vehicle");
                    setVehicleType("");
                  }}
                />{" "}
                Vehicle
              </label>
              <label>
                <input
                  type="radio"
                  value="Department"
                  checked={vehicleDept === "Department"}
                  onChange={() => {
                    setVehicleDept("Department");
                    setVehicleType("");
                  }}
                />{" "}
                Department
              </label>
            </div>

            {vehicleDept === "Vehicle" && (
              <div className="flex gap-4 mt-2 ml-4">
                <label>
                  <input
                    type="radio"
                    value="H"
                    checked={vehicleType === "H"}
                    onChange={() => setVehicleType("H")}
                  />{" "}
                  Hire
                </label>
                <label>
                  <input
                    type="radio"
                    value="O"
                    checked={vehicleType === "O"}
                    onChange={() => setVehicleType("O")}
                  />{" "}
                  Own
                </label>
              </div>
            )}

            {isEditMode && (
              <div className="flex gap-6 mt-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isClosed}
                    onChange={(e) => handleCloseChange(e.target.checked)}
                    disabled={isCancelled}
                  />
                  Close
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={isCancelled}
                    onChange={(e) => handleCancelChange(e.target.checked)}
                    disabled={isClosed}
                  />
                  Cancel
                </label>
              </div>
            )}
          </div>

          <div>
            <label className="block mb-1">Employee</label>
            <select
              value={employee}
              onChange={(e) => setEmployee(e.target.value)}
              className="w-full border px-2 py-1"
            >
              <option value="">Select</option>
              {employeeList.map((emp, idx) => (
                <option key={idx} value={emp.fld_Employeeid}>
                  {emp.fld_Employee}
                </option>
              ))}
            </select>

            <label className="block mt-3">Indent Ref</label>
            <input
              type="text"
              value={indentRef}
              onChange={(e) => setIndentRef(e.target.value)}
              className="w-full border px-2 py-1"
            />

            <label className="block mt-3">Remarks</label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              className="w-full border px-2 py-1"
              rows={3}
            />
          </div>
        </div>

        <div className="border-t pt-6 mt-6">
          <h2 className="font-bold text-lg mb-4">FILTRATION AREA</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
            <div>
              <label>Item Category</label>
              <select
                value={itemCategory}
                onChange={(e) => setItemCategory(e.target.value)}
                className="w-full border px-2 py-1"
              >
                <option value="">All</option>
                {itemCategories.map((cat, idx) => (
                  <option key={idx} value={cat.fld_itemcategoryid}>
                    {cat.fld_itemcategory_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label>Item Name</label>
              <input
                type="text"
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                className="w-full border px-2 py-1"
              />
            </div>

            <div>
              <label>Item Code</label>
              <input
                type="text"
                value={itemCode}
                onChange={(e) => setItemCode(e.target.value)}
                className="w-full border px-2 py-1"
              />
            </div>
          </div>

          <div className="mb-4 flex items-center">
            <button
              onClick={handleSearch}
              className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded transition-colors`}
            >
              <FaSearch /> Search
            </button>
          </div>

          {searchResults.length > 0 ? (
            <div className="overflow-x-auto mb-6">
              <table className="min-w-full text-sm border border-gray-300">
                <thead className={`bg-${baseColor}-${baseShade} text-white`}>
                  <tr>
                    <th className="border px-3 py-2 text-left">S.No</th>
                    <th className="border px-3 py-2 text-left">Item Name</th>
                    <th className="border px-3 py-2 text-left">Item Code</th>
                    <th className="border px-3 py-2 text-center">Select</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="border px-3 py-2">{index + 1}</td>
                      <td className="border px-3 py-2">{item.fld_itemname}</td>
                      <td className="border px-3 py-2">{item.fld_itemcode}</td>
                      <td className="border px-3 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={selectedItems.some(
                            (i) =>
                              String(i.fld_itemcode) ===
                              String(item.fld_itemcode_raw || item.fld_itemcode)
                          )}
                          onChange={(e) => handleItemSelect(e, item)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 italic mb-6">
              No results yet. Run a search.
            </p>
          )}

          <div className="overflow-x-auto mt-4 border rounded">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className={`bg-${baseColor}-${baseShade} text-white`}>
                <tr>
                  <th className="px-4 py-2">S.No</th>
                  <th className="px-4 py-2">Item Name</th>
                  <th className="px-4 py-2">Item Code</th>
                  <th className="px-4 py-2">Part No</th>
                  <th className="px-4 py-2">UOM</th>
                  <th className="px-4 py-2">Available Qty</th>
                  <th className="px-4 py-2">Qty *</th>
                  <th className="px-4 py-2">Due Date *</th>
                  <th className="px-4 py-2">Department Code *</th>
                  {vehicleDept !== "Department" && (
                    <th className="px-4 py-2">Vehicle Code</th>
                  )}
                  <th className="px-4 py-2">Remarks</th>
                  <th className="px-4 py-2">Select</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {selectedItems.length > 0 ? (
                  selectedItems.map((item, index) => (
                    <tr key={item.fld_itemid || index} className="text-center">
                      <td className="px-4 py-2">{index + 1}</td>
                      <td className="px-4 py-2">{item.fld_itemname}</td>
                      <td className="px-4 py-2">{item.fld_itemcode}</td>
                      <td className="px-4 py-2">{item.fld_itemPartNo || "-"}</td>
                      <td className="px-4 py-2">{item.fld_uomname}</td>
                      <td className="px-4 py-2">
                        {item.fld_closing_stock !== "" &&
                        item.fld_closing_stock !== null &&
                        item.fld_closing_stock !== undefined
                          ? item.fld_closing_stock
                          : "-"}
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          value={item.fld_Qty || ""}
                          onChange={(e) =>
                            updateSelectedItem(index, "fld_Qty", e.target.value)
                          }
                          className="w-20 border rounded px-2 py-1"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="date"
                          value={item.fld_duedate || ""}
                          onChange={(e) =>
                            updateSelectedItem(index, "fld_duedate", e.target.value)
                          }
                          className="border rounded px-2 py-1"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={item.fld_departmentcode || ""}
                          onChange={(e) =>
                            updateSelectedItem(
                              index,
                              "fld_departmentcode",
                              e.target.value
                            )
                          }
                          className="border rounded px-2 py-1"
                        />
                      </td>
                      {vehicleDept !== "Department" && (
                        <td className="px-4 py-2">
                          <input
                            type="text"
                            value={item.fld_vehichlecode || ""}
                            onChange={(e) =>
                              updateSelectedItem(
                                index,
                                "fld_vehichlecode",
                                e.target.value
                              )
                            }
                            className="border rounded px-2 py-1"
                          />
                        </td>
                      )}
                      <td className="px-4 py-2">
                        <input
                          type="text"
                          value={item.fld_Remarks || ""}
                          onChange={(e) =>
                            updateSelectedItem(index, "fld_Remarks", e.target.value)
                          }
                          className="border rounded px-2 py-1"
                        />
                      </td>
                      <td className="px-4 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={item.selected || false}
                          onChange={(e) =>
                            handleRowCheckboxChange(e, item.fld_itemid)
                          }
                          className="cursor-pointer"
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      className="px-4 py-2 text-center"
                      colSpan={vehicleDept !== "Department" ? 12 : 11}
                    >
                      No records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            <button
              onClick={handleAdd}
              className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded transition-colors`}
            >
              <FaPlus /> {isEditMode ? "Update" : "Add"}
            </button>
          </div>
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default StoreRequisitionAdd;